import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageIcon } from "@/components/PageIcon";
import { ForkliftInspectionDialog } from "@/components/ForkliftInspectionDialog";

const RESULT = { passed: ["Passed", "bg-emerald-50 text-emerald-700"], defects_reported: ["Defects Reported", "bg-rose-100 text-rose-700"] };
const sel = "rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm";

function Filters({ f, setF, forklifts }) {
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="forklift-filters">
      <select className={sel} value={f.forklift} onChange={set("forklift")} data-testid="filter-forklift"><option value="">All forklifts</option>{forklifts.map((x) => <option key={x.legacy_id} value={x.legacy_id}>{x.title}</option>)}</select>
      <Input className="w-48" placeholder="Inspector" value={f.inspector} onChange={set("inspector")} data-testid="filter-inspector" />
      <Input className="w-40" type="date" value={f.date_from} onChange={set("date_from")} data-testid="filter-date-from" />
      <Input className="w-40" type="date" value={f.date_to} onChange={set("date_to")} data-testid="filter-date-to" />
      <select className={sel} value={f.result} onChange={set("result")} data-testid="filter-result"><option value="">All results</option><option value="passed">Passed</option><option value="defects_reported">Defects Reported</option></select>
    </div>
  );
}

function StatusStrip({ forklifts }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4" data-testid="forklift-status-strip">
      {forklifts.map((x) => (
        <span key={x.legacy_id} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${x.status === "do_not_use" ? "bg-rose-600 text-white" : "bg-emerald-50 text-emerald-700"}`} data-testid={`forklift-status-${x.legacy_id}`}>
          {x.title}: {x.status === "do_not_use" ? "DO NOT USE" : "OK to use"}
        </span>
      ))}
    </div>
  );
}

function Row({ r, onOpen }) {
  const [label, cls] = RESULT[r.result] || [r.result, ""];
  const bad = r.result === "defects_reported";
  return (
    <tr className={`border-t border-slate-100 ${bad ? "bg-rose-50/60" : ""}`} data-testid={`forklift-row-${r.id}`}>
      <td className="px-4 py-3">{r.inspection_date} {r.inspection_time || ""}</td>
      <td className="px-4 py-3 font-medium">{r.forklift}</td>
      <td className="px-4 py-3">{r.inspector}</td>
      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-md text-xs font-bold ${cls}`}>{label}</span></td>
      <td className={`px-4 py-3 ${bad ? "font-bold text-rose-700" : ""}`}>{r.fail_count}</td>
      <td className="px-4 py-3 text-xs">{bad ? (r.reviewed ? "Reviewed" : "Awaiting review") : "Submitted"}</td>
      <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => onOpen(r.id)} data-testid={`forklift-view-${r.id}`}>View</Button></td>
    </tr>
  );
}

export default function ForkliftChecksPage() {
  const [f, setF] = useState({ forklift: "", inspector: "", date_from: "", date_to: "", result: "" });
  const [data, setData] = useState({ items: [], forklifts: [] });
  const [open, setOpen] = useState(null);
  const load = useCallback(() => { api.get("/forklift-checks", { params: f }).then((r) => setData(r.data)).catch(() => setData({ items: [], forklifts: [] })); }, [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="forklift-checks-page">
      <div className="flex items-center gap-3 mb-4"><PageIcon />
        <div><h1 className="text-2xl font-bold text-slate-900">Forklift Check Sheets</h1>
          <p className="text-sm text-slate-500">Pre-use inspections submitted from the Field app and TedOps. Defects keep a forklift Do Not Use until a manager signs it off.</p></div>
      </div>
      <StatusStrip forklifts={data.forklifts} />
      <Filters f={f} setF={setF} forklifts={data.forklifts} />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="forklift-table">
          <thead className="text-left text-xs uppercase text-slate-500"><tr>{["Date & time", "Forklift", "Inspector", "Result", "Failed items", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {data.items.map((r) => <Row key={r.id} r={r} onOpen={setOpen} />)}
            {!data.items.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500" data-testid="forklift-empty">No forklift checks found</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <ForkliftInspectionDialog id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
